import { useState, useRef, WheelEvent, MouseEvent } from 'react';
import SEO from '../components/SEO';

const workImages = [
  '/images/examples/airline.jpg',
  '/images/examples/hat.jpg',
  '/images/examples/solo.jpg',
  '/images/examples/owner_client.jpg',
  '/images/examples/pink_dress.jpg',
  '/images/examples/solo_glam.jpg',
  '/images/examples/solo_grey.jpg',
  '/images/examples/christmas.jpg',
  '/images/examples/wedding_couple.jpg',
];

export default function Work() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const imagesPerView = 3;
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const isScrolling = useRef(false);
  
  // Drag state
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentX = useRef(0);

  const goToNext = () => {
    setCurrentIndex((prev) => 
      prev + imagesPerView >= workImages.length ? 0 : prev + imagesPerView
    );
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => 
      prev - imagesPerView < 0 ? Math.max(0, workImages.length - imagesPerView) : prev - imagesPerView
    );
  };

  const currentPage = Math.floor(currentIndex / imagesPerView);

  // Handle trackpad horizontal swipe with lock
  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    // Only handle horizontal swipes (deltaX)
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      
      // If already scrolling, ignore
      if (isScrolling.current) return;
      
      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Lock scrolling
      isScrolling.current = true;

      // Debounce the scroll
      scrollTimeout.current = setTimeout(() => {
        if (e.deltaX > 0) {
          // Swipe left (go next)
          if (currentIndex + imagesPerView < workImages.length) {
            goToNext();
          }
        } else if (e.deltaX < 0) {
          // Swipe right (go prev)
          if (currentIndex > 0) {
            goToPrev();
          }
        }
        
        // Unlock after transition completes (700ms)
        setTimeout(() => {
          isScrolling.current = false;
        }, 700);
      }, 50);
    }
  };

  // Click and drag handlers
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    isDragging.current = true;
    startX.current = e.pageX;
    currentX.current = e.pageX;
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    e.preventDefault();
    currentX.current = e.pageX;
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const diff = startX.current - currentX.current;
    const threshold = 50; // Minimum drag distance to trigger navigation

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentIndex + imagesPerView < workImages.length) {
        // Dragged left, go next
        goToNext();
      } else if (diff < 0 && currentIndex > 0) {
        // Dragged right, go prev
        goToPrev();
      }
    }
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      handleMouseUp();
    }
  };

  const getObjectPosition = (index: number) => {
    if (index === 0) return '68% 100%'; // Airline - shift right AND down
    if (index === 1) return 'center 0%'; // hat - shift down
    if (index === 2) return 'center 0%'; // solo - shift down
    if (index === 3) return 'center 100%'; // Owner_client - move up
    if (index === 4) return 'center 0%'; // pink_dress - move down
    if (index === 5) return 'center 13%'; // solo_glam - move down
    if (index === 6) return 'center 100%'; // solo_grey - move up
    if (index === 7) return 'center 0%'; // Christmas - shift down
    if (index === 8) return 'center 100%'; // wedding_couple - shift down
    return undefined;
  };

  const imageAltTexts = [
    'Professional makeup for airline crew member - commercial beauty photography',
    'Luxury bridal makeup with elegant hat accessory for outdoor wedding',
    'Natural glam makeup for solo portrait session',
    'Makeup artist Laura with satisfied client after luxury makeup application',
    'Elegant makeup for pink formal dress photoshoot',
    'High-fashion solo glam makeup look for editorial shoot',
    'Soft natural makeup for grey toned portrait photography',
    'Festive holiday makeup look for Christmas event',
    'Bridal makeup for wedding couple outdoor ceremony'
  ];

  return (
    <>
      <SEO
        title="Work - Makeup Portfolio & Gallery | Atlanta"
        description="View our portfolio of luxury makeup artistry in Atlanta including bridal makeup, editorial looks, event glam, and professional makeup services. Real client transformations and beauty work."
        keywords="makeup portfolio Atlanta, bridal makeup examples Atlanta, wedding makeup gallery, editorial makeup photos, professional makeup artist work, luxury makeup transformations Atlanta"
      />
      <section className="fixed top-0 left-0 w-full h-dvh bg-gradient-to-br from-[#f1ede7] via-[#ece6de] to-[#e4ded4]
      text-[#161616] pt-20 sm:pt-25 md:pt-[150px] pb-[62px] xsm:pb-[70px] sm:pb-[68px] md:pb-16 lg:pb-14 overflow-y-auto">
      <div className="mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-8 xl:px-12 lg:h-[calc(100vh-206px)] lg:flex lg:items-center">
        
        {/* Mobile/Tablet: Vertical Grid */}
        <div className="lg:hidden w-full pt-9 xs:pt-10 xsm:pt-[43px] xsm2:pt-[52px] sm:pt-[62px] sm2:pt-[66px] md:pt-[52px]">
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
            {workImages.map((src, index) => (
              index === 5 ? null : (
                <figure
                  key={src}
                  className="group relative overflow-hidden border border-[#d9cbb8] bg-[#ece6de] 
                  shadow-[0_25px_60px_-45px_rgba(17,17,17,0.9)] backdrop-blur"
                >
                  <img
                    src={src}
                    alt={imageAltTexts[index]}
                    className="h-full w-full object-cover transition duration-300 ease-out group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                    style={getObjectPosition(index) ? { objectPosition: getObjectPosition(index) } : undefined}
                  />
                </figure>
              )
            ))}
          </div>
        </div>

        {/* Desktop: Carousel */}
        <div 
          className="hidden lg:flex w-full h-full items-center relative mt-[15px] gap-6"
          onWheel={handleWheel}
        >
          {/* Left Arrow */}
          <button
            onClick={goToPrev}
            disabled={currentIndex === 0}
            className={`shrink-0 border border-[#2a2115] bg-black text-zinc-200 p-3
            transition-all duration-300 backdrop-blur-sm
            ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:bg-[#dfd2bf] hover:text-[#2a2115]'}`}
            aria-label="Previous images"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeWidth={3} d="M18 20L6 12 18 4" />
            </svg>
          </button>

          {/* Images Container - Clean margins, no overlap with arrows */}
          <div 
            className="flex-1 overflow-hidden cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div 
              className="flex transition-transform duration-700 ease-in-out gap-6 select-none"
              style={{ transform: `translateX(-${currentPage * 100}%)` }}
            >
              {workImages.map((src, index) => (
                <figure
                  key={src}
                  className="group relative overflow-hidden border border-[#cbbda9] bg-[#ece6de] 
                   backdrop-blur shrink-0"
                  style={{ width: 'calc(33.333% - 20px)' }}
                >
                  <img
                    src={src}
                    alt={imageAltTexts[index]}
                    className="h-[650px] w-full object-cover transition duration-500 ease-out group-hover:scale-105 pointer-events-none"
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    style={getObjectPosition(index) ? { objectPosition: getObjectPosition(index) } : undefined}
                  />
                </figure>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          <button
            onClick={goToNext}
            disabled={currentIndex + imagesPerView >= workImages.length}
            className={`shrink-0 z-10 border border-[#2a2115] bg-black text-zinc-200 p-3
            transition-all duration-300 backdrop-blur-sm
            ${currentIndex + imagesPerView >= workImages.length ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:bg-[#dfd2bf] hover:text-[#2a2115]'}`}
            aria-label="Next images"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeWidth={3} d="M6 4L18 12 6 20" />
            </svg>
          </button>
        </div>
      </div>
      </section>
    </>
  );
}